'use client';

import { Tabs as TabsPrimitive } from '@base-ui/react/tabs';
import * as React from 'react';

import { cn } from '@playbooks/ui/lib/utils';

import { cva, VariantProps } from 'class-variance-authority';

const tabsListVariants = cva('inline-flex h-9 w-fit items-center text-foreground-nav', {
	variants: {
		variant: {
			default: 'rounded-none bg-transparent p-0',
			lifted: 'items-end gap-0.5 bg-transparent p-0',
		},
	},
	defaultVariants: {
		variant: 'default',
	},
});

const tabsTriggerVariants = cva(
	'inline-flex h-9 flex-1 items-center justify-center gap-1.5 whitespace-nowrap text-sm font-medium text-foreground-nav outline-none transition-colors hover:text-foreground focus-visible:ring-3 focus-visible:ring-focus/50 disabled:pointer-events-none disabled:opacity-50 data-active:text-foreground',
	{
		variants: {
			variant: {
				default:
					'rounded-none! border-x-0 border-t-0 border-b-2! border-b-transparent bg-transparent px-2 hover:bg-surface-hover data-active:border-b-foreground! data-active:bg-transparent! data-active:shadow-none',
				lifted:
					'rounded-t-panel! border border-border bg-surface px-3 hover:bg-surface-hover data-active:border-border-strong data-active:border-b-0! data-active:bg-surface-control',
			},
		},
		defaultVariants: {
			variant: 'default',
		},
	},
);

function Tabs({ className, ...props }: TabsPrimitive.Root.Props) {
	// Theme
	const rootClassName = cn('flex flex-col gap-2', className);

	// Render
	return <TabsPrimitive.Root data-slot='tabs' className={rootClassName} {...props} />;
}

type TabsListProps = TabsPrimitive.List.Props & VariantProps<typeof tabsListVariants>;
function TabsList({ className, variant, ...props }: TabsListProps) {
	// Theme
	const rootClassName = cn(tabsListVariants({ variant }), className);

	// Render
	return (
		<TabsPrimitive.List
			data-slot='tabs-list'
			data-variant={variant || 'default'}
			className={rootClassName}
			{...props}
		/>
	);
}

type TabsTriggerProps = TabsPrimitive.Tab.Props & VariantProps<typeof tabsTriggerVariants>;
function TabsTrigger({ className, variant, ...props }: TabsTriggerProps) {
	// Theme
	const rootClassName = cn(tabsTriggerVariants({ variant }), className);

	// Render
	return (
		<TabsPrimitive.Tab
			data-slot='tabs-trigger'
			data-variant={variant || 'default'}
			className={rootClassName}
			{...props}
		/>
	);
}

function TabsContent({ className, ...props }: TabsPrimitive.Panel.Props) {
	// Theme
	const rootClassName = cn('flex-1 outline-none', className);

	// Render
	return <TabsPrimitive.Panel data-slot='tabs-content' className={rootClassName} {...props} />;
}

export { Tabs, TabsContent, TabsList, TabsTrigger, tabsListVariants, tabsTriggerVariants };
